import moment from "moment";
import {VehicleType} from "./interfaces/VehicleType";

export const handleUrlWithHashtag = () => {
    // This function is meant to accommodate the old urls on www.24rent.fi temporarily
    // The url usually has the path followed by "/#" then url params which cannot be accessed after #
    // This function removes the "/#" from string so url params may be used
    let windowUrl = window.location.href;
    if (!windowUrl.includes('/#/') && !windowUrl.includes('/#?')){
        // Note: useLocation.search() was previously used to get this string in main files - this is same concept
        return window.location.search;
    } else {
        let newUrl = windowUrl.replace('/#', '')
        const parsedUrl = new URL(newUrl);
        return parsedUrl.search;
    }
}

export function processModelId(modelId: string | undefined): number[] | undefined {
    // validate and format url model id params
    if (modelId) {
        const modelIds = modelId.split(',').map(id => id.trim()); // split and trim
        const validNumbers = modelIds
            .map(id => Number(id))
            .filter(num => !isNaN(num) && Number.isInteger(num)); // remove invalid numbers and non-integers

        return validNumbers.length > 0 ? validNumbers : undefined; // array of valid numbers or undefined
    }
    return undefined; // modelId is not provided
}

// Same comma-separated convention as modelId (?acriss=1,3,5). Unrecognised codes are dropped
// individually rather than voiding the whole param, and repeats are dropped, so App's single/multi
// decisions (carModel, van segment) see the same codes the filter does: `?acriss=5,x` acts like
// `?acriss=5`, and a link can't push anything but a known code into the search API's carModel.
export function processAcrissCodes(acriss: string | undefined): string[] | undefined {
    if (acriss) {
        const codes = [...new Set(acriss.split(',').map(code => code.trim())
            .filter(code => Object.prototype.hasOwnProperty.call(ACRISS_CODE_TO_VEHICLE_TYPE, code)))];
        return codes.length > 0 ? codes : undefined;
    }
    return undefined;
}

// Finds the element that actually scrolls the given node, or null when the
// document itself is the scroller.
//
// RRM runs in two very different embeddings:
//  - standalone (rent.24rent.fi — what the mobile launcher's WebView loads):
//    App.scss pins `#r24-App-Main` to `height: 100vh`, so the document never
//    scrolls and `.pageContainer` (overflow-y: scroll) is the real scroller.
//  - shortcode on 24rent.fi: the Divi theme overrides both, `#r24-App-Main`
//    grows with its content and the window is the scroller.
// Scrolling `window` unconditionally is therefore a silent no-op standalone,
// which is why paginating search results in the launcher app left the user
// stranded at the bottom of the previous page.
const getScrollParent = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement;
    // stop below <body>: body/html scrolling is the document scroll, which the
    // window fallback below already handles (and which the 24rent.fi embed relies on)
    while (parent && parent !== document.body && parent !== document.documentElement) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
            && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
};

// Jumps the page back to the top, whichever element happens to be the scroller.
// Both calls are safe to make unconditionally: standalone the window is not
// scrollable at all, and in the 24rent.fi shortcode embed `.pageContainer` is
// not a scroll container, so exactly one of them does the work.
export const scrollPageToTop = () => {
    const pageContainer = document.querySelector<HTMLElement>('.pageContainer');
    if (pageContainer && pageContainer.scrollHeight > pageContainer.clientHeight) {
        pageContainer.scrollTo({top: 0});
    }
    window.scrollTo(0, 0);
};

export const scrollToElementWithOffset = (element: HTMLElement, offset: number) => {
    // allows for smooth scrolling to element with offset (i.e., this helps with login/greeting banner at top of screen)
    const scrollParent = getScrollParent(element);
    if (scrollParent) {
        const offsetPosition = element.getBoundingClientRect().top
            - scrollParent.getBoundingClientRect().top
            + scrollParent.scrollTop
            - offset;
        scrollParent.scrollTo({top: Math.max(offsetPosition, 0), behavior: 'smooth'});
        return;
    }
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - offset;
    window.scrollTo({top: offsetPosition, behavior: 'smooth'});
};

export function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (/chrome|crios|crmo/i.test(userAgent) && !/edge|edg|opr|opera/i.test(userAgent)) {
        return "Chrome";
    } else if (/firefox|fxios/i.test(userAgent)) {
        return "Firefox";
    } else if (/safari/i.test(userAgent) && !/chrome|crios|crmo/i.test(userAgent)) {
        return "Safari";
    } else if (/edg/i.test(userAgent)) {
        return "Edge";
    } else if (/opr|opera/i.test(userAgent)) {
        return "Opera";
    } else {
        return `Unknown - (${userAgent})`;
    }
}

// Returns the URL parameter as a string and removes it from the URL after usage
export const getAndClearUrlStringValue = (param: string): string | null => {
    const url = new URL(window.location.href);
    const value = url.searchParams.get(param);

    // nothing to clear
    if (value === null) return null;

    url.searchParams.delete(param);

    // use a same-origin relative URL to avoid cross-origin DOMException
    const newUrl = url.pathname + url.search + url.hash;
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;

    try {
        if (newUrl !== currentUrl) {
            window.history.replaceState({}, '', newUrl);
        }
    } catch (err) {
        // handle SecurityError or other failures without crashing
        console.log("replaceState failed:", err);
    }

    return value;
};

// `startdate` / `enddate` link formats: DD.MM.YYYY HH:mm (leading zeros optional), time or seconds optional.
// The Finnish dot time (09.00) and slash dates were read by the old lenient parse and may be in live
// campaign links, so they stay accepted.
const URL_DATE_FORMATS = [
    'D.M.YYYY H:mm', 'D.M.YYYY H:mm:ss', 'D.M.YYYY H.mm', 'D.M.YYYY',
    'D/M/YYYY H:mm', 'D/M/YYYY H.mm', 'D/M/YYYY',
];

// Returns the URL parameter as a moment object and removes it from the URL after usage
export const getAndClearUrlTimeValue = (param: string): moment.Moment | null => {
    const url = new URL(window.location.href);
    const value = url.searchParams.get(param);

    // nothing to clear
    if (value === null) return null;

    url.searchParams.delete(param);

    // use a same-origin relative URL to avoid cross-origin DOMException
    const newUrl = url.pathname + url.search + url.hash;
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;

    try {
        if (newUrl !== currentUrl) {
            window.history.replaceState({}, '', newUrl);
        }
    } catch (err) {
        console.log("replaceState failed:", err);
    }

    if (!value) return null;
    // strict, so junk like `startdate=abc` or `12abc` is ignored instead of becoming a (partial) date
    const parsed = moment(value, URL_DATE_FORMATS, true);
    return parsed.isValid() ? parsed : null;
};

export function formatTemplate(template: string, vars: Record<string, string>): string {
    return template.replace(/\$\{([^}]+)\}/g, (_m, key) => vars[key] ?? '');
}

export const resetUrlForPaymentRetry = (): void => {
    const url = new URL(window.location.href);

    if (url.searchParams.get('retry') === 'true') return;

    url.searchParams.set('retry', 'true');

    const newUrl = url.pathname + url.search + url.hash;
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;

    try {
        if (newUrl !== currentUrl) {
            window.history.replaceState({}, '', newUrl);
        }
    } catch (err) {
        console.warn('replaceState failed:', err);
    }
};

export const resetUrlForResCancelled = (): void => {
    const url = new URL(window.location.href);

    // no-op if retry already equals 'true'
    if (url.searchParams.get('resCancelled') === 'true') return;

    // add or overwrite retry=true without removing other params
    url.searchParams.set('resCancelled', 'true');

    const newUrl = url.pathname + url.search + url.hash;
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;

    try {
        if (newUrl !== currentUrl) {
            window.history.replaceState({}, '', newUrl); // app state handles page refresh
        }
    } catch (err) {
        console.warn('replaceState failed:', err);
    }
};

export const resetRrmUrl = (): void => {
    const url = new URL(window.location.href);
    const lang = url.searchParams.get('lang');

    // remove all params then restore lang if present
    url.search = '';
    if (lang !== null) {
        url.searchParams.set('lang', lang);
    }

    const newUrl = url.pathname + url.search + url.hash;
    const currentUrl = window.location.pathname + window.location.search + window.location.hash;

    try {
        if (newUrl !== currentUrl) {
            window.history.replaceState({}, '', newUrl);
        }
    } catch (err) {
        console.warn('replaceState failed:', err);
    }
};
const ACRISS_CODE_TO_VEHICLE_TYPE: Record<string, VehicleType> = {
    '1': VehicleType.Compact,
    '2': VehicleType.StationWagon,
    '3': VehicleType.SUV,
    '4': VehicleType.Minibus,
    '5': VehicleType.Van,
};

/**
 * Body types for the `acriss` URL codes. Unrecognised codes are dropped individually rather
 * than voiding the whole param; only the table's own keys count as codes.
 */
export function vehicleTypesFromAcrissCodes(codes: string[] | undefined): VehicleType[] | undefined {
    const types = (codes ?? [])
        .filter(code => Object.prototype.hasOwnProperty.call(ACRISS_CODE_TO_VEHICLE_TYPE, code))
        .map(code => ACRISS_CODE_TO_VEHICLE_TYPE[code]);
    return types.length > 0 ? types : undefined;
}

/**
 * The single code forwarded to the search API's `carModel`. Multiple codes are filtered
 * client-side instead, so a multi-value link must not inherit an earlier single-code API
 * filter from the session, or the API would pre-filter to the wrong body type.
 */
export function searchVehicleTypeFromAcriss(codes: string[] | undefined, previous: string | undefined): string | undefined {
    if (!codes?.length) return previous;
    return codes.length === 1 ? codes[0] : undefined;
}

export const MAX_URL_DAYS = 60;        // RRM refuses reservations longer than 60 days
export const MAX_URL_HOURS = 60 * 24;

/**
 * Rental length from a `days` / `hours` link parameter: a whole number within bounds, or 0
 * when missing or junk — a bad value must never produce an invalid end date.
 */
export function parseLengthParam(value: string | null | undefined, max: number): number {
    if (value === null || value === undefined) return 0;
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return 0;
    const parsed = parseInt(trimmed, 10);
    return parsed >= 1 && parsed <= max ? parsed : 0;
}
